/**
 * The internal docs corpus, for surfaces that are reached by BOTH audiences.
 *
 * ## The problem this solves
 *
 * A brand user searches the customer corpus. A staff user searches both. That is one sentence
 * of product behaviour and it cannot be implemented with a static import, because the only
 * component that needs it — the ⌘K palette — is eagerly routed off `Layout`, so a static
 * `import { INTERNAL_DOCS_CATALOG }` there would put every internal guide's title, excerpt and
 * heading text into the bundle that every brand customer downloads on their first page load.
 * `docs/sources.internal.ts` documents at length why that is the failure mode to design
 * against (working-conventions §43): splitting BODIES into a lazy chunk buys nothing if the
 * summary of those bodies rides along in the eager graph, and an excerpt is guide text.
 *
 * So the import is dynamic AND gated. Dynamic keeps the manifest out of the eager chunk;
 * gated means the fetch only happens for a user the predicate has already said is staff, so a
 * brand session never requests the chunk at all. Neither half is redundant: dynamic-but-
 * ungated would still ship the bytes to a customer's browser the moment they opened search,
 * and gated-but-static would ship them to everyone regardless.
 *
 * ## What it is NOT
 *
 * Not access control, for the same reason `sources.internal.ts` gives: the chunk is a static
 * asset and its URL is reachable by anyone who guesses it. This decides what a browser fetches
 * by DEFAULT. The standing constraint is unchanged — internal guides may be operationally
 * sensitive, never secret.
 */
import { useEffect, useState } from 'react'

import { useIsInternalUser } from 'hooks/useIsInternalUser'

import type { DocEntry } from './catalog'

/**
 * Stable identity for "no internal docs", so a consumer can put the result in a `useMemo`
 * dependency list without re-running on every render.
 */
const NONE: DocEntry[] = []

/**
 * Every internal guide, or an empty list for anyone who is not Vera staff.
 *
 * Empty on the first render for a staffer too — the manifest arrives a tick later, when the
 * chunk resolves. That is deliberate and harmless here: the palette re-renders on the state
 * change, and a search typed within that first tick simply returns customer results and then
 * gains internal ones. Blocking the palette on a network fetch to avoid a sub-frame gap would
 * be a worse trade.
 */
export const useInternalDocs = (): DocEntry[] => {
  // Carries the `!inSpace` clause: a staffer operating inside a brand's space IS that brand's
  // user, so they get the brand's corpus. Same reason the internal nav hides in that state.
  const isInternal = useIsInternalUser()
  const [docs, setDocs] = useState<DocEntry[]>(NONE)

  useEffect(() => {
    if (!isInternal) return
    let live = true
    void import('./internal').then(m => {
      if (live) setDocs(m.INTERNAL_DOCS_CATALOG.docs)
    })
    return () => {
      live = false
    }
  }, [isInternal])

  // Read through the predicate rather than returning `docs` directly. Entering a brand's space
  // flips `isInternal` false while the already-loaded manifest is still sitting in state, and a
  // staffer acting as a brand must not keep getting internal results.
  return isInternal ? docs : NONE
}
